import { useMemo, useState } from "react";
import { CopyCodeButton } from "@/components/CopyCode";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, ShieldAlert, ChevronDown, ChevronRight, Filter, Download, Clock, Siren, Unlock, ScrollText } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { api, API_URL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate, formatBs, formatUsd, formatCop } from "@/lib/format";

const ACTION_STYLES = {
  LOGIN_SUCCESS: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  LOGIN_FAIL: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  LOGIN_LOCKED: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  LOGOUT: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  TICKET_VOID: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  TICKET_LIMIT_EXCEEDED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  TICKET_PAY: "bg-primary/15 text-primary border-primary/40",
  RATE_SYNC: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  RATE_SYNC_FAIL: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  RATE_SET_MANUAL: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  USER_CREATE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  USER_UPDATE: "bg-slate-500/15 text-slate-200 border-slate-500/40",
  USER_DELETE: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  USER_PASSWORD_CHANGE: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  FRAUD_ALERT_TRIGGERED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  FRAUD_ALERT_RESOLVED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  TAQUILLA_AUTO_SUSPENDED_INACTIVITY: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};

const ACTION_LABEL = {
  LOGIN_SUCCESS: "Login exitoso",
  LOGIN_FAIL: "Login fallido",
  LOGIN_LOCKED: "Cuenta bloqueada",
  LOGOUT: "Cierre de sesión",
  TICKET_VOID: "Anulación de ticket",
  TICKET_LIMIT_EXCEEDED: "Límite de venta excedido",
  TICKET_PAY: "Pago de premio",
  RATE_SYNC: "Sync tasa BCV",
  RATE_SYNC_FAIL: "Sync tasa (fallo)",
  RATE_SET_MANUAL: "Tasa manual",
  USER_CREATE: "Usuario creado",
  USER_UPDATE: "Usuario editado",
  USER_DELETE: "Usuario eliminado",
  USER_PASSWORD_CHANGE: "Cambio de contraseña",
  FRAUD_ALERT_TRIGGERED: "Alerta antifraude",
  FRAUD_ALERT_RESOLVED: "Alerta resuelta",
  TAQUILLA_AUTO_SUSPENDED_INACTIVITY: "Suspensión por inactividad",
};

function ActionBadge({ action }) {
  const cls = ACTION_STYLES[action] || "bg-slate-500/15 text-slate-300 border-slate-500/30";
  return (
    <Badge
      variant="outline"
      className={`${cls} font-mono text-[11px] uppercase tracking-wide`}
      data-testid={`audit-action-${action}`}
    >
      {ACTION_LABEL[action] || action}
    </Badge>
  );
}

function MetadataView({ meta }) {
  if (!meta || Object.keys(meta).length === 0) {
    return <span className="text-xs text-muted-foreground italic">Sin metadatos</span>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs">
      {Object.entries(meta).map(([k, v]) => (
        <div key={k} className="flex gap-2">
          <span className="text-muted-foreground min-w-[110px]">{k}:</span>
          <span className="font-mono text-foreground/90 break-all">
            {typeof v === "object" ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

function Row({ e, onOpenTimeline }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        className="fintech-row cursor-pointer"
        onClick={() => setOpen((v) => !v)}
        data-testid={`audit-row-${e.id}`}
      >
        <td className="py-2 pl-3 pr-2 w-4">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="py-2 pr-3 whitespace-nowrap text-xs text-muted-foreground font-mono">
          {formatDate(e.created_at)}
        </td>
        <td className="py-2 pr-3">
          <ActionBadge action={e.action} />
        </td>
        <td className="py-2 pr-3 text-sm">
          <div className="font-medium">{e.actor_username || "—"}</div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {e.actor_role || "—"}
          </div>
        </td>
        <td className="py-2 pr-3 font-mono text-xs">
          {e.ticket_code ? (
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={(ev) => { ev.stopPropagation(); onOpenTimeline?.(e.ticket_code); }}
              data-testid={`audit-ticket-${e.ticket_code}`}
              title="Ver timeline del ticket"
            >
              {e.ticket_code}
            </button>
          ) : null}
          {e.ticket_code ? (
            <CopyCodeButton code={e.ticket_code} size={12} className="ml-0.5" />
          ) : e.entity_id ? (
            <span className="text-muted-foreground">{e.entity_type}:{e.entity_id.slice(0, 8)}…</span>
          ) : (
            "—"
          )}
        </td>
        <td className="py-2 pr-3 font-mono text-xs text-muted-foreground">
          {e.actor_ip || "—"}
        </td>
      </tr>
      {open && (
        <tr className="bg-slate-50 border-b border-slate-200/80 dark:bg-slate-800/30 dark:border-slate-800">
          <td colSpan={6} className="px-6 py-3">
            <MetadataView meta={e.metadata} />
            {e.user_agent && (
              <div className="mt-2 text-[11px] text-muted-foreground truncate">
                <span className="text-muted-foreground/70">User-Agent: </span>
                <span className="font-mono">{e.user_agent}</span>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function AuditLogView() {
  const [q, setQ] = useState("");
  const [action, setAction] = useState("__all__");
  const [limit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [qInput, setQInput] = useState("");
  const [timelineCode, setTimelineCode] = useState(null);
  const [exporting, setExporting] = useState(false);

  const { data: actions = [] } = useQuery({
    queryKey: ["audit", "actions"],
    queryFn: async () => (await api.get("/audit-log/actions")).data,
  });

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (action && action !== "__all__") p.set("action", action);
    p.set("limit", String(limit));
    p.set("offset", String(offset));
    return p.toString();
  }, [q, action, limit, offset]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["audit", params],
    queryFn: async () => (await api.get(`/audit-log?${params}`)).data,
    keepPreviousData: true,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const hasNext = offset + limit < total;
  const hasPrev = offset > 0;

  const applySearch = (e) => {
    e?.preventDefault?.();
    setOffset(0);
    setQ(qInput.trim());
  };
  const clearFilters = () => {
    setQInput("");
    setQ("");
    setAction("__all__");
    setOffset(0);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/audit-log/export.csv?${params}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "").slice(0, 15)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Exportación descargada");
    } catch (err) {
      toast.error("No se pudo exportar el CSV");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="audit-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <ShieldAlert size={22} className="text-primary" /> Auditoría
          </h1>
          <p className="text-sm text-muted-foreground">
            Trazabilidad de eventos críticos: logins, anulaciones, pagos, sincronización de tasa, gestión de usuarios.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={exporting}
            data-testid="audit-export-csv"
          >
            <Download size={14} className="mr-2" />
            {exporting ? "Exportando…" : "Exportar CSV"}
          </Button>
          <Badge variant="outline" className="font-mono">
            {total} eventos
          </Badge>
        </div>
      </div>

      <Card className="p-3 md:p-4 fintech-card">
        <form onSubmit={applySearch} className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[240px]">
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1 block">
              Buscar por serial de ticket o usuario
            </label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Ej. TEB4-QECE-TUX o admin"
                className="pl-9"
                data-testid="audit-search-input"
              />
            </div>
          </div>
          <div className="min-w-[220px]">
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1 block">
              Acción
            </label>
            <Select value={action} onValueChange={(v) => { setAction(v); setOffset(0); }}>
              <SelectTrigger data-testid="audit-action-filter">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas</SelectItem>
                {actions.map((a) => (
                  <SelectItem key={a} value={a}>
                    {ACTION_LABEL[a] || a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" variant="default" data-testid="audit-search-button">
            <Filter size={14} className="mr-2" /> Buscar
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={clearFilters}
            data-testid="audit-clear-filters-button"
          >
            Limpiar
          </Button>
        </form>
      </Card>

      <Card className="p-0 overflow-hidden fintech-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm fintech-table" data-testid="audit-table">
            <thead className="bg-secondary/40 text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left py-2 pl-3 pr-2 w-4"></th>
                <th className="text-left py-2 pr-3">Fecha</th>
                <th className="text-left py-2 pr-3">Acción</th>
                <th className="text-left py-2 pr-3">Actor</th>
                <th className="text-left py-2 pr-3">Ticket / Entidad</th>
                <th className="text-left py-2 pr-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-muted-foreground">
                    Cargando…
                  </td>
                </tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-muted-foreground" data-testid="audit-empty">
                    Sin eventos que coincidan con el filtro.
                  </td>
                </tr>
              )}
              {items.map((e) => (
                <Row key={e.id} e={e} onOpenTimeline={setTimelineCode} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200/80 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/60">
          <div className="text-xs text-muted-foreground">
            Mostrando {items.length ? offset + 1 : 0}–{offset + items.length} de {total}
            {isFetching ? " · actualizando…" : ""}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!hasPrev}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              data-testid="audit-prev-page"
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext}
              onClick={() => setOffset(offset + limit)}
              data-testid="audit-next-page"
            >
              Siguiente
            </Button>
          </div>
        </div>
      </Card>

      <TicketTimelineDialog
        code={timelineCode}
        onClose={() => setTimelineCode(null)}
      />
    </div>
  );
}

function TicketTimelineDialog({ code, onClose }) {
  const open = !!code;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["audit", "ticket-timeline", code],
    queryFn: async () => (await api.get(`/audit-log/ticket/${encodeURIComponent(code)}`)).data,
    enabled: open,
  });
  const t = data?.ticket;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl bg-card border-2 border-border p-0 overflow-hidden" data-testid="audit-timeline-dialog">
        <DialogTitle className="sr-only">Timeline del ticket {code}</DialogTitle>
        <DialogDescription className="sr-only">
          Detalle completo del ticket y sus eventos de auditoría.
        </DialogDescription>
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={18} className="text-primary" />
            <h2 className="text-xl font-bold">Timeline · <span className="font-mono">{code}</span></h2>
          </div>

          {isLoading && (
            <div className="text-sm text-muted-foreground py-6 text-center">Cargando…</div>
          )}
          {isError && (
            <div className="text-sm text-rose-400 py-6 text-center">
              No se pudo cargar el timeline. ¿Ticket fuera de tu banca?
            </div>
          )}
          {t && (
            <>
              <div className="rounded-xl border border-border bg-secondary/30 p-3 mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <Field label="Estado" value={<span className="font-mono uppercase">{t.status}</span>} />
                  <Field label="Total" value={<span className="font-mono">Bs {Number(t.total_bs || 0).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>} />
                  <Field label="Banca" value={t.banca?.name || "—"} />
                  <Field label="Taquilla" value={t.taquilla?.name || "—"} />
                  <Field label="Vendedor" value={t.vendedor?.username || "—"} />
                  <Field label="Origen" value={t.origin || "web"} />
                  <Field label="Creado" value={<span className="text-xs">{formatDate(t.created_at)}</span>} />
                  <Field label="Pago" value={t.payment_status || "—"} />
                </div>
              </div>

              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Eventos</div>
              <div className="max-h-[45vh] overflow-y-auto space-y-2">
                {(data?.events || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">Sin eventos registrados para este ticket.</div>
                ) : (
                  data.events.map((e) => (
                    <div
                      key={e.id}
                      className="rounded-lg border border-border/60 bg-secondary/20 p-3"
                      data-testid={`timeline-event-${e.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <ActionBadge action={e.action} />
                        <span className="text-xs text-muted-foreground font-mono">{formatDate(e.created_at)}</span>
                      </div>
                      <div className="text-xs">
                        Por <span className="font-semibold">{e.actor_username || "—"}</span>
                        {e.actor_role && <span className="text-muted-foreground"> · {e.actor_role}</span>}
                        {e.actor_ip && <span className="text-muted-foreground font-mono ml-2">({e.actor_ip})</span>}
                      </div>
                      {e.metadata && Object.keys(e.metadata).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-border/40">
                          <MetadataView meta={e.metadata} />
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

const RULES = {
  FRAUD_RULE_A_EXCESSIVE_BET: { label: "Apuesta Excesiva", cls: "bg-rose-500/15 text-rose-600 dark:text-rose-300 border-rose-500/40" },
  FRAUD_RULE_B_TICKET_TOTAL: { label: "Ticket Anómalo", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40" },
  FRAUD_RULE_C_CONCENTRATION: { label: "Concentración de Jugadas", cls: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/40" },
};
const FMT = { BS: formatBs, USD: formatUsd, COP: formatCop };
const native = (a) => (FMT[a.currency] || formatBs)(a.details?.ticket_total_native ?? a.attempted_amount_bs);

function ResolveDialog({ alert, onClose }) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState("");
  const resolve = useMutation({
    mutationFn: async () => (await api.post(`/audit-log/security-alerts/${alert.id}/resolve`, { notes: notes.trim() || null })).data,
    onSuccess: () => {
      toast.success(`Taquilla ${alert.taquilla_code} desbloqueada`);
      qc.invalidateQueries({ queryKey: ["security-alerts"] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo resolver la alerta"),
  });
  return (
    <Dialog open={!!alert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="security-alert-resolve-dialog">
        <DialogHeader>
          <DialogTitle>Aprobar y desbloquear taquilla</DialogTitle>
          <DialogDescription>
            Confirmas que revisaste la operación de <span className="font-mono">{alert?.taquilla_code}</span> ({RULES[alert?.rule_code]?.label}) por {alert && native(alert)} {alert?.currency}. La taquilla saldrá de la suspensión preventiva.
          </DialogDescription>
        </DialogHeader>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} placeholder="Observaciones (opcional)" data-testid="security-alert-resolve-notes" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="security-alert-resolve-cancel">Cancelar</Button>
          <Button onClick={() => resolve.mutate()} disabled={resolve.isPending} data-testid="security-alert-resolve-confirm">
            <Unlock size={15} className="mr-1.5" />{resolve.isPending ? "Desbloqueando…" : "Aprobar y Desbloquear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AlertDetail({ a }) {
  const d = a.details || {};
  const items = [["Umbral", d.threshold_native != null ? `${(FMT[a.currency] || formatBs)(d.threshold_native)} ${a.currency}` : "—"],
    ["Jugadas en ticket", d.plays_count], d.distinct_numbers && ["Números distintos (mismo sorteo)", d.distinct_numbers],
    d.plays?.length && ["Jugadas sospechosas", d.plays.map((p) => `${p.number} · ${(FMT[a.currency] || formatBs)(p.amount_native)}`).join(", ")],
    a.resolution_notes && ["Observaciones", a.resolution_notes], a.resolved_by_username && ["Resuelta por", `${a.resolved_by_username} · ${formatDate(a.resolved_at)}`]].filter(Boolean);
  return <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs">{items.map(([k, v]) => <div key={k} className="flex gap-2"><span className="text-muted-foreground min-w-[150px]">{k}:</span><span className="font-mono break-all">{String(v)}</span></div>)}</div>;
}

function AlertRow({ a, onResolve }) {
  const [open, setOpen] = useState(false);
  const rule = RULES[a.rule_code] || { label: a.rule_code, cls: "" };
  return (
    <>
      <tr className="fintech-row cursor-pointer" onClick={() => setOpen((v) => !v)} data-testid={`security-alert-row-${a.id}`}>
        <td className="py-2 pl-3 pr-2 w-4">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td className="py-2 pr-3 whitespace-nowrap text-xs text-muted-foreground font-mono">{formatDate(a.created_at)}</td>
        <td className="py-2 pr-3"><Badge variant="outline" className={`${rule.cls} text-[11px]`} data-testid={`security-alert-rule-${a.id}`}>{rule.label}</Badge></td>
        <td className="py-2 pr-3 text-sm"><div className="font-mono whitespace-nowrap">{a.taquilla_code}</div><div className="text-[11px] text-muted-foreground">{a.agencia_name || "—"}</div></td>
        <td className="py-2 pr-3 text-right font-mono tabular-nums whitespace-nowrap" data-testid={`security-alert-amount-${a.id}`}>{native(a)} <span className="text-[10px] text-muted-foreground">{a.currency}</span></td>
        <td className="py-2 pr-3 text-right">
          {a.status === "pending_review" ? (
            <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onResolve(a); }} data-testid="security-alert-resolve-btn">
              <Unlock size={14} className="mr-1.5" />Aprobar y Desbloquear Taquilla
            </Button>
          ) : <Badge variant="outline" className="text-emerald-600 dark:text-emerald-300 border-emerald-500/40">Resuelta</Badge>}
        </td>
      </tr>
      {open && <tr className="bg-slate-50 dark:bg-slate-800/30"><td colSpan={6} className="px-6 py-3"><AlertDetail a={a} /></td></tr>}
    </>
  );
}

function SecurityAlertsPanel() {
  const [status, setStatus] = useState("pending_review");
  const [target, setTarget] = useState(null);
  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ["security-alerts", status],
    queryFn: async () => (await api.get(`/audit-log/security-alerts?status=${status}`)).data,
    refetchInterval: 20000,
  });
  return (
    <div className="space-y-4" data-testid="security-alerts-panel">
      <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2"><Siren size={22} className="text-rose-500" /> Alertas de Seguridad</h1>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">Taquillas en suspensión preventiva por patrones atípicos (Reglas A, B y C). Revisa el detalle y aprueba para reanudar la operación.</p>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-60" data-testid="security-alerts-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending_review">Pendientes de revisión</SelectItem>
            <SelectItem value="resolved">Resueltas</SelectItem>
            <SelectItem value="all">Todas</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Card className="fintech-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="security-alerts-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground border-b border-border">
              <th className="py-2 pl-3" /><th className="py-2 pr-3">Fecha (Caracas)</th><th className="py-2 pr-3">Regla</th><th className="py-2 pr-3">Taquilla / Agencia</th><th className="py-2 pr-3 text-right">Monto intentado</th><th className="py-2 pr-3 text-right">Acción</th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Cargando…</td></tr>}
              {!isLoading && alerts.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground" data-testid="security-alerts-empty">Sin alertas {status === "pending_review" ? "pendientes" : ""}</td></tr>}
              {alerts.map((a) => <AlertRow key={a.id} a={a} onResolve={setTarget} />)}
            </tbody>
          </table>
        </div>
      </Card>
      {target && <ResolveDialog alert={target} onClose={() => setTarget(null)} />}
    </div>
  );
}

export default function AuditPage() {
  const [tab, setTab] = useState("log");
  const { data: pending = [] } = useQuery({
    queryKey: ["security-alerts", "pending_review"],
    queryFn: async () => (await api.get("/audit-log/security-alerts?status=pending_review")).data,
    refetchInterval: 20000,
  });
  const tabCls = (t) => `inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${tab === t ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="audit-tabs">
        <button type="button" className={tabCls("log")} onClick={() => setTab("log")} data-testid="audit-nav-log"><ScrollText size={15} />Bitácora</button>
        <button type="button" className={tabCls("alerts")} onClick={() => setTab("alerts")} data-testid="audit-nav-security-alerts">
          <Siren size={15} />Alertas de Seguridad
          {pending.length > 0 && <span className="ml-1 rounded-full bg-rose-600 text-white text-[11px] font-bold px-2 py-0.5" data-testid="security-alerts-pending-count">{pending.length}</span>}
        </button>
      </div>
      {tab === "log" ? <AuditLogView /> : <SecurityAlertsPanel />}
    </div>
  );
}
